import { useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { ETabs, FC, Props } from "./typings";
import workspaceService from "api/workspaceService";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { getPrivilages } from "utils/getPrivilages";
import { IWorkspace } from "dto/IWorkspace";
import CardIcon from "assets/icons/credit_card.svg?react";
import MemberCard from "./MemberCard";
import MemberSubscriptionPanel from "./MemberSubscriptionPanel";
import PaymentMethods from "./PaymentMethods";
import {
  ESubscriptionMode,
  ISubscriptionMember,
  ISubscriptionsView,
} from "dto/ISubscriptionsView";
import { formatPrice, getMonthlyCost } from "utils/appPlans";
import { EAppVariant } from "dto/EAppVariant";
import { EAppExtension } from "dto/EAppExtension";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import { ENotificationType, notification } from "utils/notification";
import { IParcelsSummary } from "dto/IParcelsSummary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import PaymentsSummaryCard from "./PaymentsSummaryCard";
import PaymentStatusBadge from "./PaymentStatusBadge";
import { IPaymentListItem, IPaymentsListView } from "dto/IPayment";
import { IInvoice } from "dto/IInvoice";
import InvoiceCard from "./InvoiceCard";

const Plans = ({
  goToCard,
  restricted,
}: {
  goToCard: () => void;
  restricted?: boolean;
}) => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: subscriptionsData, refetch } =
    workspaceService.useGetSubscriptions(workspaceId);

  const view: ISubscriptionsView = useMemo(
    () => subscriptionsData?.data,
    [subscriptionsData],
  );

  const mode = view?.Mode ?? ESubscriptionMode.Private;
  const isTeam = mode === ESubscriptionMode.Team;

  // Tryb zespołowy: zmiany odkładamy lokalnie i zatwierdzamy zbiorczo jednym „Zapisz".
  const [staged, setStaged] = useState<
    Record<string, { Variant: EAppVariant; Extensions: EAppExtension[] }>
  >({});

  const { mutate: saveBatch, isLoading: savingBatch } =
    workspaceService.useUpdateSubscriptionsBatch();

  const members = useMemo(
    () =>
      [...(view?.Members ?? [])]
        // Członek bez uprawnień do edycji zespołu widzi na liście tylko siebie.
        .filter((m) => !restricted || m.IsMe)
        .sort((a, b) => {
          if (a.IsMe && !b.IsMe) return -1;
          if (!a.IsMe && b.IsMe) return 1;
          return a.Label.localeCompare(b.Label);
        })
        .map((m) => {
          const change = staged[m.UserId];
          if (!change) return m;
          return {
            ...m,
            Variant: change.Variant,
            Extensions: change.Extensions,
            MonthlyCost: getMonthlyCost(change.Variant, change.Extensions),
          };
        }),
    [view, staged, restricted],
  );

  const [editing, setEditing] = useState<ISubscriptionMember | null>(null);

  const stagedCount = Object.keys(staged).length;

  const handleSaveBatch = () => {
    saveBatch(
      {
        WorkspaceId: view?.WorkspaceId,
        Changes: Object.entries(staged).map(([userId, c]) => ({
          MemberUserId: userId,
          Variant: c.Variant,
          Extensions: c.Extensions,
        })),
      },
      {
        onSuccess: (resp) => {
          setStaged({});
          // H4: zmiana wymaga płatności, a zespół nie ma karty → przejście do dodania karty.
          if (resp?.data?.RequiresPaymentMethod) {
            notification({
              type: ENotificationType.INFO,
              title: "Dodaj kartę",
              text: "Aby aktywować pakiety zespołu, dodaj kartę płatniczą — płatność zostanie pobrana automatycznie.",
            });
            goToCard();
            return;
          }
          notification({
            type: ENotificationType.SUCCESS,
            title: "Subskrypcje",
            text: "Zapisano zmiany subskrypcji zespołu.",
          });
          refetch();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Subskrypcje",
            text: "Nie udało się zapisać zmian.",
          }),
      },
    );
  };

  if (editing) {
    return (
      <MemberSubscriptionPanel
        member={editing}
        mode={mode}
        workspaceId={view?.WorkspaceId}
        onBack={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refetch();
        }}
        onRequireCard={goToCard}
        onStage={
          isTeam
            ? (variant, extensions) => {
                setStaged((prev) => ({
                  ...prev,
                  [editing.UserId]: {
                    Variant: variant,
                    Extensions: extensions,
                  },
                }));
                setEditing(null);
              }
            : undefined
        }
      />
    );
  }

  return (
    <div className={styles.tab}>
      <div className={styles.header}>Plany</div>
      <div className={styles.hint}>
        {restricted
          ? "Twój pakiet i moduły w ramach zespołu. Opłatę ponosi właściciel zespołu."
          : mode === ESubscriptionMode.Team
            ? "Skonfiguruj pakiet i moduły dla poszczególnych członków zespołu. Opłatę ponosi właściciel zespołu."
            : "Zarządzaj swoim pakietem i dokupionymi modułami. Zakończenie konta doprowadzi do trwałego skasowania danych — przed nim pobierz dane za pomocą funkcji eksportu."}
      </div>
      <div>
        <a href="https://notaup.pl/cennik" target="_blank">
          Zobacz cennik
        </a>
      </div>
      <div className={styles.list}>
        <div className={styles.header}>
          <div className={styles.name}>Użytkownik</div>
          <div className={styles.subscription}>Plan</div>
          <div className={styles.extensions}>Rozszerzenia</div>
          <div className={styles.sum}>Suma</div>
          <div className={styles.more}></div>
        </div>
        <div className={styles.data}>
          {members.map((member) => (
            <MemberCard
              key={member.UserId}
              member={member}
              onEdit={setEditing}
            />
          ))}
        </div>
      </div>

      <div className={styles.vatNote}>
        Podane ceny są cenami netto — zostanie do nich doliczony podatek VAT 23%.
      </div>

      {isTeam && stagedCount > 0 && (
        <div className={styles.saveBar}>
          <HollowButton
            text="Odrzuć zmiany"
            onClick={() => setStaged({})}
          />
          <Button
            text={`Zapisz zmiany (${stagedCount})`}
            onClick={handleSaveBatch}
            loading={savingBatch}
          />
        </div>
      )}
    </div>
  );
};

const Settlements = () => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: summaryData } =
    workspaceService.useGetPaymentsSummary(workspaceId);
  const { data: paymentsData, refetch: refetchPayments } =
    workspaceService.useGetPayments(workspaceId);

  const summary: IParcelsSummary = useMemo(
    () => summaryData?.data,
    [summaryData],
  );

  const paymentsView: IPaymentsListView | undefined = useMemo(
    () => paymentsData?.data,
    [paymentsData],
  );

  // Status płatności podpięty do okresu rozliczeniowego po kluczu rok-miesiąc
  // (jedna płatność na profil rozliczeniowy w danym okresie).
  const paymentByPeriod = useMemo(() => {
    const map = new Map<string, IPaymentListItem>();
    (paymentsView?.Items ?? []).forEach((p) => {
      map.set(`${p.Year}-${p.Month}`, p);
    });
    return map;
  }, [paymentsView]);

  // Zwijanie grup okresów. Domyślnie rozwinięte (brak klucza = rozwinięty).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = (key: string) =>
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className={styles.list}>
      <div className={styles.header}>
        <div className={styles.name}>Użytkownik</div>
        <div className={styles.edelivery}>Plan</div>
        <div className={styles.nota}>Przesyłki</div>
        <div className={styles.total}>Suma</div>
        <div className={styles.status}>Status</div>
        <div className={styles.more}></div>
      </div>
      {summary?.Periods && (
        <div className={styles.data}>
          {summary.Periods.map((p) => {
            const key = `${p.Year}-${p.Month}`;
            const isOpen = !collapsed[key];
            const payment = paymentByPeriod.get(key);
            // Suma okresu = autorytatywna kwota z płatności (brutto), gdy płatność istnieje;
            // inaczej fallback do brutto przeliczonego z summary (BE). Eliminuje rozjazd płatność↔summary.
            const periodAmount = payment ? payment.Amount : p.TotalCost ?? 0;
            return (
              <div key={key} className={styles.period}>
                <div className={styles.header} onClick={() => toggle(key)}>
                  <div className={styles.name}>{p.Label}</div>
                  <div className={styles.edelivery}>
                    {formatPrice(periodAmount)}
                  </div>
                  <div className={styles.nota}>
                    <div className={styles.cnt}>{p.Nota}</div>
                  </div>
                  <div className={styles.total}>
                    {formatPrice(periodAmount)}
                  </div>
                  <div
                    className={styles.status}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PaymentStatusBadge
                      payment={payment}
                      publishableKey={paymentsView?.PublishableKey}
                      paymentMethodId={paymentsView?.PaymentMethodId}
                      onConfirmed={refetchPayments}
                    />
                  </div>
                  <div className={styles.more}>
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      className={`${styles.caret} ${
                        !isOpen ? styles.caretCollapsed : ""
                      }`}
                    />
                  </div>
                </div>

                {isOpen &&
                  p.Items.map((i) => (
                    <PaymentsSummaryCard
                      key={i.UserId}
                      item={i}
                      workspaceId={workspaceId}
                      year={p.Year}
                      month={p.Month}
                    />
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Invoices = () => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: summaryData } = workspaceService.useGetInvoices(workspaceId);

  const invoices: IInvoice[] = useMemo(
    () => summaryData?.data ?? [],
    [summaryData],
  );

  return (
    <div className={styles.list}>
      <div className={styles.header}>
        <div className={styles.name}>Nazwa</div>
        <div className={styles.edelivery}>Data</div>
        <div className={styles.nota}>Kwota</div>
        <div className={styles.total}></div>
        <div className={styles.more}></div>
      </div>
      <div className={styles.data}>
        {invoices.map((i) => (
          <InvoiceCard key={i.Id} item={i} />
        ))}
      </div>
    </div>
  );
};

const PAYMENT_TAB_PARAM: Record<string, ETabs> = {
  plans: ETabs.PLANS,
  settlements: ETabs.SETTLEMENTS,
  invoices: ETabs.INVOICES,
  payments: ETabs.PAYMENTS,
};

const PaymentsTab: FC<Props> = () => {
  const [workspaceId] = useState(getWorkspaceId() ?? "");
  const { data: workspaceData } = workspaceService.useGetWorkspace(workspaceId);
  const workspace = workspaceData?.data as IWorkspace | undefined;
  const me = workspace?.Members?.find((m) => m.IsMe === true);
  const meRole = workspace?.Roles?.find((r) => r.Id === me?.RoleId);
  const myPrivilages = getPrivilages(meRole);

  // Profil zespołu = aktywny profil ma WorkspaceId. Członek bez uprawnienia do edycji
  // zespołu widzi tylko „Plany" (bez Rozliczeń/Faktur/Karty) — opłaca je właściciel zespołu.
  const isTeam = !!workspaceId;
  const restricted = isTeam && !myPrivilages.CanEditMembersInWorkspace;

  // Deep-link z maila (np. „Potwierdź płatność"): ?paymentTab=settlements otwiera od razu Rozliczenia.
  const [actualView, setActualView] = useState<ETabs>(() => {
    const t = new URLSearchParams(window.location.search).get("paymentTab");
    return t && t in PAYMENT_TAB_PARAM ? PAYMENT_TAB_PARAM[t] : ETabs.PLANS;
  });

  // Gdy dostęp ograniczony, deep-link do ukrytej zakładki ignorujemy — zostają „Plany".
  const view = restricted ? ETabs.PLANS : actualView;

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <CardIcon></CardIcon>Płatności
      </div>
      <div className={styles.menu}>
        <div
          onClick={() => setActualView(ETabs.PLANS)}
          className={`${styles.box}  ${
            view === ETabs.PLANS && styles.activeText
          }`}
        >
          Plany
        </div>
        {!restricted && (
          <>
            <div className={styles.line}></div>
            <div
              onClick={() => setActualView(ETabs.SETTLEMENTS)}
              className={`${styles.box}  ${
                view === ETabs.SETTLEMENTS && styles.activeText
              }`}
            >
              Rozliczenia
            </div>
            <div className={styles.line}></div>
            <div
              onClick={() => setActualView(ETabs.INVOICES)}
              className={`${styles.box}  ${
                view === ETabs.INVOICES && styles.activeText
              }`}
            >
              Faktury
            </div>
            <div className={styles.line}></div>
            <div
              onClick={() => setActualView(ETabs.PAYMENTS)}
              className={`${styles.box}  ${
                view === ETabs.PAYMENTS && styles.activeText
              }`}
            >
              Karta płatnicza
            </div>
          </>
        )}
      </div>

      {view === ETabs.PLANS && (
        <Plans
          goToCard={() => setActualView(ETabs.PAYMENTS)}
          restricted={restricted}
        />
      )}
      {view === ETabs.SETTLEMENTS && <Settlements />}
      {view === ETabs.INVOICES && <Invoices />}
      {view === ETabs.PAYMENTS && <PaymentMethods />}
    </div>
  );
};

export default PaymentsTab;
